import { Pipe, PipeTransform, Injectable } from '@angular/core';
import { Agency } from '@ag-common-lib/public-api';
import { AgentChangesService, AgentSearchService } from 'ag-common-svc/public-api';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { filter, map, mergeMap, startWith, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
@Pipe({ name: 'agentNameByDbId' })
export class AgentNameByDbIdePipe implements PipeTransform {
  private _agentId$ = new BehaviorSubject(null);
  private _allowPrimaryEmail$ = new BehaviorSubject(false);
  agentFullName$ = this._agentId$.pipe(
    filter(Boolean),
    mergeMap(agentDbId => {
      if (!agentDbId) {
        return '';
      }
      // Re-resolved when a signal names this agent, which is what replaces the document
      // listener this pipe used to open per distinct id — on every grid row that rendered
      // a name.
      return this.agentChangesService.agentChanged$(agentDbId).pipe(
        startWith(undefined),
        switchMap(() => from(this.agentSearchService.getById(agentDbId))),
        map(data => {
          if (!data) {
            return '';
          }

          const allowPrimaryEmail = this._allowPrimaryEmail$.value;
          const fullAgentName = [data?.p_agent_first_name, data?.p_agent_last_name].filter(Boolean).join(' ');
          const primaryEmail = data?.p_email ?? '';
          return allowPrimaryEmail && primaryEmail ? `${fullAgentName} (${primaryEmail})` : fullAgentName;
        }),
      );
    }),
  );

  constructor(
    private agentSearchService: AgentSearchService,
    private agentChangesService: AgentChangesService,
  ) {}

  transform(agentDbId: string, showPrimaryEmail: boolean = false, converter?: (agency: Agency) => string): Observable<string> {
    this._agentId$.next(agentDbId);
    this._allowPrimaryEmail$.next(showPrimaryEmail);

    return this.agentFullName$;
  }
}
